import { RliSdk } from '@rocketlane/rli-internal-sdk';
import { useLocationSearch } from '@rocketlane/rocket-router';
import { useCallback, useMemo } from 'react';
import { useCustomerPortalHashMap } from 'components/organisms/customer-portal/utils';
import { TaskEvents, TaskEventsMap } from 'events/tasks';
import { logger } from 'services';

export const useQueryParams = () => {
  const search = useLocationSearch();
  const val = useMemo(() => new URLSearchParams(search), [search]);

  return val;
};

export const useQueryParamsFromWindow = () => {
  const val = useMemo(() => new URLSearchParams(window.location.search), []);

  return val;
};

export const useTaskDrawerQueryParamsFromRouter = () => {
  const { hashMap, removeKeys, batchUpdate } = useCustomerPortalHashMap();

  const closeModal = useCallback(() => {
    removeKeys(['task_drawer', 'task_drawer_tab', 'td_approvals', 'reply_to']);
  }, [removeKeys]);

  const taskDrawer = useMemo(() => {
    const td = hashMap?.task_drawer;

    if (!td) {
      return {
        taskId: undefined,
        projectId: undefined,
      };
    }
    const [projectId, taskId] = td.split('.');

    return {
      taskId: parseInt(taskId, 10),
      projectId: parseInt(projectId, 10),
    };
  }, [hashMap]);

  const openTaskDrawer = useCallback(
    (
      projectId: number,
      taskId: number,
      tab?: string,
      args?: {
        formAnswerId?: number;
        replyTo?: number;
      },
    ) => {
      const updates: Record<string, string> = {
        task_drawer: `${projectId}.${taskId}`,
        task_drawer_tab: tab ?? 'conversations',
      };

      if (args?.formAnswerId) {
        updates.td_form_answer_id = args.formAnswerId.toString();
      }
      if (args?.replyTo) {
        updates.reply_to = args.replyTo.toString();
      }

      batchUpdate(updates, { replace: false, state: { fromTaskOpen: true } });
      try {
        RliSdk.emitToAllWidgets(TaskEventsMap[TaskEvents.OpenTaskDrawer], {
          projectId,
          taskId,
        });
      } catch (error) {
        logger.error('Failed to publish open task drawer event', { error });
      }
    },
    [batchUpdate],
  );

  return { taskDrawer, closeModal, openTaskDrawer };
};

export const useSprintDrawerQueryParamsFromRouter = () => {
  const { hashMap, removeKeys, batchUpdate } = useCustomerPortalHashMap();

  const closeModal = useCallback(() => {
    removeKeys(['sprint_drawer']);
  }, [removeKeys]);

  const sprintDrawer = useMemo(() => {
    const sd = hashMap?.sprint_drawer;

    if (!sd) {
      return {
        sprintId: undefined,
        projectId: undefined,
      };
    }
    const [projectId, sprintId] = sd.split('.');

    return {
      sprintId: parseInt(sprintId, 10),
      projectId: parseInt(projectId, 10),
    };
  }, [hashMap]);

  const openSprintDrawer = useCallback(
    (projectId: number, sprintId: number) => {
      batchUpdate(
        { sprint_drawer: `${projectId}.${sprintId}` },
        { replace: false, state: { fromSprintOpen: true } },
      );
    },
    [batchUpdate],
  );

  return { sprintDrawer, closeModal, openSprintDrawer };
};
